import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, MessageCircle, CheckCircle2, PenTool, Box, Wrench,
  ChevronDown, Send, ChefHat, Coffee, Building2, Palmtree, Cross, Wheat, Utensils, ShoppingCart, Cloud, Factory,
} from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { CATEGORIES, EMIRATES, WHATSAPP_URL } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { Kitchen3DViewer } from "../components/Kitchen3DViewer";
import { useSiteImages } from "../hooks/use-site-images";

const HERO_IMG = "https://images.pexels.com/photos/2977514/pexels-photo-2977514.jpeg";
const BLUEPRINT_IMG = "https://images.pexels.com/photos/37320179/pexels-photo-37320179.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const RENDER_3D_IMG = "https://images.unsplash.com/photo-1749478072094-d21cb929490c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwa2l0Y2hlbiUyMHN0YWlubGVzcyUyMHN0ZWVsfGVufDB8fHx8MTc4MjMxOTA3OXww&ixlib=rb-4.1.0&q=85";
const EXECUTION_IMG = "https://images.pexels.com/photos/358549/pexels-photo-358549.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CATEGORY_ICONS = {
  restaurant: Utensils, cafe: Coffee, hotel: Building2, resort: Palmtree,
  hospital: Cross, bakery: Wheat, laundry: Wrench, catering: ChefHat,
  supermarket: ShoppingCart, cloud_kitchen: Cloud, central_kitchen: Factory,
};

const BUDGET_OPTIONS = ["under_50k", "50k_150k", "150k_500k", "500k_plus"];
const TIMEFRAME_OPTIONS = ["asap", "1_3_months", "3_6_months", "planning"];

export default function Design() {
  const { t, isRTL } = useLang();
  const siteImage = useSiteImages();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  const stages = [
    { icon: PenTool, image: siteImage("design_blueprint", BLUEPRINT_IMG), ...t.design.stages[0] },
    { icon: Box, image: siteImage("design_3d_render", RENDER_3D_IMG), ...t.design.stages[1] },
    { icon: Wrench, image: siteImage("design_execution", EXECUTION_IMG), ...t.design.stages[2] },
  ];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="design-page" className="bg-white">
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={siteImage("design_hero", HERO_IMG)} alt="Kitchen design" className="w-full h-full object-cover" />
          <div className="absolute inset-0 brand-bg-hero" />
        </div>
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <p className="text-[11px] tracking-[0.3em] text-red-200 mb-4 font-semibold" data-testid="design-eyebrow">
            {t.design.eyebrow}
          </p>
          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.1] max-w-4xl mb-5" data-testid="design-title">
            {t.design.title}
          </h1>
          <p className="text-white/85 text-base md:text-lg max-w-2xl mb-8 leading-relaxed">{t.design.subtitle}</p>
          <div className="flex flex-wrap gap-3 mb-8">
            <Button onClick={() => scrollTo("design-consultation")} data-testid="design-cta-consult"
                    className="bg-[#ef4444] hover:bg-[#dc2626] text-white h-12 px-6 text-sm font-semibold shadow-lg">
              {t.design.cta1} <Arrow className="w-4 h-4 ms-2" />
            </Button>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer">
              <Button className="bg-white/10 backdrop-blur border-white/60 border text-white hover:bg-white hover:text-[#0891b2] h-12 px-6 text-sm font-semibold" data-testid="design-cta-expert">
                <MessageCircle className="w-4 h-4 me-2" /> {t.design.cta2}
              </Button>
            </a>
          </div>
          <div className="flex flex-wrap gap-2">
            {[t.design.badge1, t.design.badge2, t.design.badge3].map((b, i) => (
              <span key={i} className="text-xs font-medium text-white/90 bg-white/10 backdrop-blur border border-white/20 rounded-full px-3 py-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 inline me-1.5 text-red-300" /> {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#334155] text-white py-10" data-testid="design-stats">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {t.design.stats.map((s, i) => (
            <div key={i} className="text-center border-s-2 border-[#ef4444]/60 ps-4" data-testid={`design-stat-${i}`}>
              <div className="font-display text-4xl lg:text-5xl text-[#ef4444] mb-1">{s.value}</div>
              <div className="text-xs uppercase tracking-widest text-slate-300">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* STAGES */}
      <section className="py-20" data-testid="design-stages">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-12 max-w-3xl">
            <p className="text-[11px] tracking-[0.3em] text-[#ef4444] mb-3 font-semibold">{t.design.stagesEyebrow}</p>
            <h2 className="font-display text-3xl sm:text-4xl text-[#334155] mb-4">{t.design.stagesTitle}</h2>
            <p className="text-slate-600 text-lg">{t.design.stagesSubtitle}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {stages.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="group bg-white border border-slate-200 hover:border-[#ef4444] rounded-lg overflow-hidden transition-colors" data-testid={`design-stage-${i}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100 relative">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-3 start-3 bg-white/95 backdrop-blur w-10 h-10 rounded-full flex items-center justify-center border border-slate-200">
                      <span className="font-display text-[#ef4444] text-sm">0{i + 1}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="w-10 h-10 bg-cyan-50 rounded flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-[#ef4444]" />
                    </div>
                    <h3 className="font-display text-lg text-[#334155] mb-2">{s.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{s.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3D INTERACTIVE VIEWER */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-slate-100" data-testid="design-3d-viewer">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-6 max-w-3xl">
            <p className="text-[11px] tracking-[0.3em] text-[#ef4444] mb-3 font-semibold">{t.design.viewer.eyebrow}</p>
            <h2 className="font-display text-3xl sm:text-4xl text-[#334155] mb-3">{t.design.viewer.title}</h2>
            <p className="text-slate-600 text-base">{t.design.viewer.subtitle}</p>
          </div>

          <Kitchen3DViewer
            title={t.design.viewer.title}
            subtitle={t.design.viewer.subtitle}
          />

          <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
            <p className="text-xs text-slate-500">{t.design.viewer.hint}</p>
            <p className="text-[11px] text-slate-400 italic">Admin can change the 3D model URL from the dashboard → Settings.</p>
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="py-16 bg-slate-50" data-testid="design-sectors">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-10 max-w-3xl mx-auto">
            <p className="text-[11px] tracking-[0.3em] text-[#ef4444] mb-3 font-semibold">{t.design.sectorsEyebrow}</p>
            <h2 className="font-display text-3xl sm:text-4xl text-[#334155] mb-3">{t.design.sectorsTitle}</h2>
            <p className="text-slate-600">{t.design.sectorsSubtitle}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {CATEGORIES.map((c) => {
              const Icon = CATEGORY_ICONS[c] || Utensils;
              return (
                <Link key={c} to={`/projects?category=${c}`} data-testid={`design-sector-${c}`}
                      className="group bg-white border border-slate-200 hover:border-[#ef4444] rounded-lg p-4 flex flex-col items-center text-center gap-2 transition-colors">
                  <Icon className="w-6 h-6 text-[#ef4444]" />
                  <div className="text-sm font-medium text-slate-700 group-hover:text-[#0891b2]">{t.categories[c]}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20" data-testid="design-process">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="mb-12 max-w-3xl">
            <p className="text-[11px] tracking-[0.3em] text-[#ef4444] mb-3 font-semibold">{t.design.processEyebrow}</p>
            <h2 className="font-display text-3xl sm:text-4xl text-[#334155]">{t.design.processTitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 relative">
            {t.design.process.map((step, i) => (
              <div key={i} className="relative bg-slate-50 border border-slate-200 rounded-lg p-5" data-testid={`design-step-${i}`}>
                <div className="w-9 h-9 bg-[#ef4444] text-white font-display rounded-full flex items-center justify-center mb-3">
                  {i + 1}
                </div>
                <h3 className="font-display text-base text-[#334155] mb-2">{step.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{step.text}</p>
                {i < t.design.process.length - 1 && (
                  <Arrow className="hidden lg:block absolute top-1/2 -end-3 w-4 h-4 text-[#ef4444]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="py-20 bg-[#334155] text-white" data-testid="design-why">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-10">
          <div>
            <p className="text-[11px] tracking-[0.3em] text-red-300 mb-3 font-semibold">{t.design.whyEyebrow}</p>
            <h2 className="font-display text-3xl sm:text-4xl mb-4">{t.design.whyTitle}</h2>
            <p className="text-slate-300 leading-relaxed mb-6">{t.design.whySubtitle}</p>
            <Button onClick={() => scrollTo("design-consultation")} data-testid="design-why-cta"
                    className="bg-[#ef4444] hover:bg-[#dc2626] text-white h-11 px-6">
              {t.design.cta1} <Arrow className="w-4 h-4 ms-2" />
            </Button>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
            {t.design.whyItems.map((it, i) => (
              <div key={i} className="bg-slate-800/60 border border-slate-700 rounded-lg p-5" data-testid={`design-why-${i}`}>
                <CheckCircle2 className="w-6 h-6 text-[#ef4444] mb-3" />
                <h3 className="font-display text-lg text-white mb-2">{it.title}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{it.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONSULTATION FORM */}
      <section id="design-consultation" className="py-20" data-testid="design-form-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <p className="text-[11px] tracking-[0.3em] text-[#ef4444] mb-3 font-semibold">{t.design.formEyebrow}</p>
            <h2 className="font-display text-3xl sm:text-4xl text-[#334155] mb-3">{t.design.formTitle}</h2>
            <p className="text-slate-600">{t.design.formSubtitle}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6 sm:p-10 shadow-sm">
            <ConsultationForm />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-slate-50" data-testid="design-faq">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-8">
            <p className="text-[11px] tracking-[0.3em] text-[#ef4444] mb-3 font-semibold">{t.design.faqEyebrow}</p>
            <h2 className="font-display text-3xl text-[#334155]">{t.design.faqTitle}</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {t.design.faq.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white border border-slate-200 rounded-lg px-5" data-testid={`design-faq-${i}`}>
                <AccordionTrigger className="text-start hover:no-underline font-display text-[#334155]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-[#ef4444] text-white" data-testid="design-final-cta">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl mb-3">{t.design.finalCtaTitle}</h2>
          <p className="text-red-100 mb-8">{t.design.finalCtaSubtitle}</p>
          <Button onClick={() => scrollTo("design-consultation")} data-testid="design-final-btn"
                  className="bg-white text-[#0891b2] hover:bg-cyan-50 h-12 px-8 font-semibold">
            {t.design.finalCta} <Arrow className="w-4 h-4 ms-2" />
          </Button>
        </div>
      </section>
    </div>
  );
}

// ============ CONSULTATION FORM ============
const emptyConsultation = {
  project_type: "restaurant", name: "", company_name: "", phone: "", email: "",
  emirate: "dubai", area_sqm: "", budget: "50k_150k", timeframe: "1_3_months", message: "",
};

function ConsultationForm() {
  const { t } = useLang();
  const [f, setF] = useState(emptyConsultation);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quotations", {
        name: f.name,
        phone: f.phone,
        project_type: f.project_type,
        emirate: f.emirate,
        message: f.message || `Design consultation for ${f.project_type}. Area: ${f.area_sqm || "N/A"} m². Budget: ${f.budget}. Timeframe: ${f.timeframe}.`,
        company_name: f.company_name,
        email: f.email,
        budget: f.budget,
        area_sqm: f.area_sqm,
        timeframe: f.timeframe,
        preferred_contact: "whatsapp",
        source: "design_consultation",
      });
      toast.success(t.design.formSuccess);
      setDone(true);
    } catch {
      toast.error(t.quotation.error);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="design-form-success">
        <CheckCircle2 className="w-16 h-16 text-[#ef4444] mx-auto mb-5" />
        <h3 className="font-display text-2xl text-[#334155] mb-3">{t.design.formSuccess}</h3>
        <Button onClick={() => { setF(emptyConsultation); setDone(false); }}
                data-testid="design-form-again"
                className="bg-[#ef4444] hover:bg-[#dc2626] text-white">Submit another</Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="design-consultation-form">
      <F label={t.design.fields.projectType} required>
        <Select value={f.project_type} onValueChange={(v) => upd("project_type", v)}>
          <SelectTrigger data-testid="d-project-type" className="h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t.categories[c]}</SelectItem>)}
          </SelectContent>
        </Select>
      </F>

      <div className="grid sm:grid-cols-2 gap-4">
        <F label={t.design.fields.name} required>
          <Input required value={f.name} onChange={(e) => upd("name", e.target.value)} data-testid="d-name" className="h-11" />
        </F>
        <F label={t.design.fields.companyName}>
          <Input value={f.company_name} onChange={(e) => upd("company_name", e.target.value)} data-testid="d-company" className="h-11" />
        </F>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <F label={t.design.fields.phone} required>
          <Input required dir="ltr" value={f.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="d-phone" className="h-11" placeholder="+971 4-288-2777" />
        </F>
        <F label={t.design.fields.email}>
          <Input type="email" value={f.email} onChange={(e) => upd("email", e.target.value)} data-testid="d-email" className="h-11" />
        </F>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <F label={t.design.fields.emirate}>
          <Select value={f.emirate} onValueChange={(v) => upd("emirate", v)}>
            <SelectTrigger data-testid="d-emirate" className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EMIRATES.map((e) => <SelectItem key={e} value={e}>{t.emirates[e]}</SelectItem>)}
            </SelectContent>
          </Select>
        </F>
        <F label={t.design.fields.area}>
          <Input value={f.area_sqm} onChange={(e) => upd("area_sqm", e.target.value)} data-testid="d-area" className="h-11" placeholder="120" />
        </F>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <F label={t.design.fields.budget}>
          <Select value={f.budget} onValueChange={(v) => upd("budget", v)}>
            <SelectTrigger data-testid="d-budget" className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BUDGET_OPTIONS.map((b) => <SelectItem key={b} value={b}>{t.design.budgets[b]}</SelectItem>)}
            </SelectContent>
          </Select>
        </F>
        <F label={t.design.fields.timeframe}>
          <Select value={f.timeframe} onValueChange={(v) => upd("timeframe", v)}>
            <SelectTrigger data-testid="d-timeframe" className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIMEFRAME_OPTIONS.map((t2) => <SelectItem key={t2} value={t2}>{t.design.timeframes[t2]}</SelectItem>)}
            </SelectContent>
          </Select>
        </F>
      </div>

      <F label={t.design.fields.details}>
        <Textarea rows={4} value={f.message} onChange={(e) => upd("message", e.target.value)} data-testid="d-message" />
      </F>

      <Button type="submit" disabled={loading} data-testid="d-submit"
              className="w-full sm:w-auto bg-[#ef4444] hover:bg-[#dc2626] text-white h-12 px-10 font-semibold">
        {loading ? t.quotation.submitting : t.design.formSubmit}
        {!loading && <Send className="w-4 h-4 ms-2" />}
      </Button>
      <p className="text-xs text-slate-500">{t.design.formNote}</p>
    </form>
  );
}

function F({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">
        {label} {required && <span className="text-[#ef4444]">*</span>}
      </Label>
      {children}
    </div>
  );
}
